import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSetUserTags, type AdminUser } from "@/hooks/useAdminUsers";

const ALLOWED_TAGS = ["internal", "beta", "friend", "do_not_email"] as const;
type AllowedTag = (typeof ALLOWED_TAGS)[number];

interface Props {
  user: AdminUser | null;
  open: boolean;
  onClose: () => void;
}

/**
 * Despite the "Popover" name in the spec, this is rendered as a small
 * Dialog: we open it programmatically from a kebab menu item (no anchor),
 * and a centered modal gives better focus trap / ESC handling than a
 * floating popover detached from its trigger.
 */
export function EditTagsPopover({ user, open, onClose }: Props) {
  const setTags = useSetUserTags();
  const [selected, setSelected] = useState<Set<AllowedTag>>(new Set());

  // Initialize selection from the user's current tags whenever the user
  // changes or the modal re-opens.
  useEffect(() => {
    if (open && user) {
      const initial = new Set<AllowedTag>();
      for (const t of user.tags) {
        if ((ALLOWED_TAGS as readonly string[]).includes(t)) {
          initial.add(t as AllowedTag);
        }
      }
      setSelected(initial);
    }
  }, [open, user?.id, user?.tags]);

  function toggle(tag: AllowedTag) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }

  function handleSave() {
    if (!user) return;
    // Preserve order of ALLOWED_TAGS for deterministic output.
    const tags = ALLOWED_TAGS.filter((t) => selected.has(t));
    setTags.mutate({ userId: user.id, tags });
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Edit tags</DialogTitle>
          <DialogDescription>
            {user ? `Manage tags for ${user.email}.` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {ALLOWED_TAGS.map((tag) => {
            const id = `tag-${tag}`;
            const labelText =
              tag === "do_not_email" ? "Do not email" : labelize(tag);
            return (
              <label
                key={tag}
                htmlFor={id}
                className="flex cursor-pointer items-center gap-3 rounded-md border p-2 text-sm hover:bg-muted/50"
              >
                <input
                  id={id}
                  type="checkbox"
                  checked={selected.has(tag)}
                  onChange={() => toggle(tag)}
                  className="size-4"
                  aria-label={labelText}
                />
                <span>{labelText}</span>
              </label>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!user}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function labelize(tag: string) {
  return tag.charAt(0).toUpperCase() + tag.slice(1);
}

export default EditTagsPopover;
